import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import PageHeader from "../components/PageHeader";
import PageContent from "../components/PageContent";
import { Card } from "../components/Card";
import MobileSimulatorDevice from "../components/MobileSimulatorDevice";
import { Input } from "../components/Input";
import { Dropdown } from "../components/Dropdown";
import { Form, FormGroup, Label } from "../components/Form";
import { TextArea } from "../components/Textarea";

const SimulatorContent = styled(PageContent)`
  margin-top: 0;
  padding-top: calc(80px + 32px);
  height: calc(100vh);
  flex-direction: row;

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    padding: 0 1rem;
    padding-top: 24px;
  }
`;

const CharacterCount = styled.div`
  font-size: 12px;
  color: ${(props) => (props.$overLimit ? "#d32f2f" : "#666")};
  margin-top: 4px;
  text-align: left;
`;

const VALID_TYPES = [
  "banner",
  "bottom-sheet",
  "push-notification",
  "notification-center"
];

const DEFAULTS = {
  type: "banner",
  bannerColor: "gold",
  bottomSheetColor: "green",
  header: "Lock your EBT card",
  subheader:
    "To prevent unauthorized use, lock your card when you're not using it. Need to buy something? Just unlock your card.",
  bannerButtonText: "Lock card now",
  bottomSheetPrimaryButtonText: "Get started",
  bottomSheetIllustration: "security",
  notificationHeader: "Title",
  notificationSubheader: "Body text",
  notificationCenterColor: "purple",
  notificationCenterUnread: true,
  notificationCenterTimestamp: "2m",
  notificationCenterIcon: "badge-percent",
  state: ""
};

function MobileSimulator({ user, onLogout, urlType }) {
  const navigate = useNavigate();

  // Determine initial type from URL or default
  const initialType = VALID_TYPES.includes(urlType) ? urlType : DEFAULTS.type;

  // State for MobileSimulatorDevice props
  const [type, setType] = useState(initialType);

  // Sync type with URL when urlType changes
  useEffect(() => {
    if (urlType && VALID_TYPES.includes(urlType) && urlType !== type) {
      setType(urlType);
    }
  }, [urlType]);

  // Update URL when type changes
  const handleTypeChange = (newType) => {
    setType(newType);
    navigate(`/mobile-simulator/${newType}`, { replace: true });
  };
  const [bannerColor, setBannerColor] = useState(DEFAULTS.bannerColor);
  const [bottomSheetColor, setBottomSheetColor] = useState(
    DEFAULTS.bottomSheetColor
  );
  const [header, setHeader] = useState(DEFAULTS.header);
  const [subheader, setSubheader] = useState(DEFAULTS.subheader);
  const [bannerButtonText, setBannerButtonText] = useState(
    DEFAULTS.bannerButtonText
  );
  const [bottomSheetPrimaryButtonText, setBottomSheetPrimaryButtonText] =
    useState(DEFAULTS.bottomSheetPrimaryButtonText);
  const [bottomSheetIllustration, setBottomSheetIllustration] = useState(
    DEFAULTS.bottomSheetIllustration
  );
  const [notificationHeader, setNotificationHeader] = useState(
    DEFAULTS.notificationHeader
  );
  const [notificationSubheader, setNotificationSubheader] = useState(
    DEFAULTS.notificationSubheader
  );
  const [notificationCenterColor, setNotificationCenterColor] = useState(
    DEFAULTS.notificationCenterColor
  );
  const [notificationCenterUnread, setNotificationCenterUnread] = useState(
    DEFAULTS.notificationCenterUnread
  );
  const [notificationCenterTimestamp, setNotificationCenterTimestamp] = useState(
    DEFAULTS.notificationCenterTimestamp
  );
  const [notificationCenterIcon, setNotificationCenterIcon] = useState(
    DEFAULTS.notificationCenterIcon
  );
  const [state, setState] = useState(DEFAULTS.state);

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleHeaderChange = (e) => setHeader(e.target.value);
  const handleSubheaderChange = (e) => setSubheader(e.target.value);
  const handleButtonTextChange = (e) => setBannerButtonText(e.target.value);
  const handleBottomSheetPrimaryButtonTextChange = (e) =>
    setBottomSheetPrimaryButtonText(e.target.value);
  const handleNotificationHeaderChange = (e) =>
    setNotificationHeader(e.target.value);
  const handleNotificationSubheaderChange = (e) =>
    setNotificationSubheader(e.target.value);
  const handleStateChange = (e) => setState(e.target.value);

  const typeOptions = [
    { value: "banner", label: "Banner" },
    { value: "bottom-sheet", label: "Bottom Sheet" },
    { value: "push-notification", label: "Push Notification" },
    { value: "notification-center", label: "Notification Center" }
  ];

  const bannerColorOptions = [
    { value: "green", label: "Promotional" },
    { value: "gold", label: "Warning" },
    { value: "red", label: "Alert" }
  ];

  const bottomSheetColorOptions = [
    { value: "green", label: "Promotional" },
    { value: "gold", label: "Warning" },
    { value: "red", label: "Alert" },
    { value: "blue", label: "Information" },
    { value: "purple", label: "Security" }
  ];

  const bottomSheetIllustrationOptions = [
    { value: "security", label: "Security" },
    { value: "money", label: "Money" },
    { value: "weather", label: "Weather" }
  ];

  const notificationCenterColorOptions = [
    { value: "purple", label: "Purple" },
    { value: "blue", label: "Blue" },
    { value: "green", label: "Green" },
    { value: "gold", label: "Gold" },
    { value: "red", label: "Red" }
  ];

  const notificationCenterTimestampOptions = [
    { value: "2m", label: "2m" },
    { value: "4h", label: "4h" },
    { value: "8d", label: "8d" }
  ];

  const notificationCenterIconOptions = [
    { value: "badge-percent", label: "Badge Percent" },
    { value: "landmark", label: "Landmark" },
    { value: "bell", label: "Bell" },
    { value: "gift", label: "Gift" },
    { value: "calendar", label: "Calendar" },
    { value: "credit-card", label: "Credit Card" },
    { value: "shield", label: "Shield" },
    { value: "heart", label: "Heart" },
    { value: "star", label: "Star" },
    { value: "circle-info", label: "Info" },
    { value: "triangle-exclamation", label: "Warning" },
    { value: "circle-check", label: "Check" },
    { value: "tag", label: "Tag" },
    { value: "money-bill", label: "Money" }
  ];

  const stateOptions = [
    { value: "", label: "Select a state" },
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
    { value: "DC", label: "District of Columbia" }
  ];

  return (
    <>
      <PageHeader
        title="Propel App Simulator"
        showBackButton={true}
        onBack={handleBack}
      />
      <SimulatorContent $fullWidth>
        <Card style={{ flex: "1", overflowY: "auto" }}>
          <Form>
            <FormGroup>
              <Label htmlFor="type">Type</Label>
              <Dropdown
                id="type"
                name="type"
                value={type}
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Dropdown>
            </FormGroup>

            {type === "banner" && (
              <FormGroup>
                <Label htmlFor="banner-variant">Variant</Label>
                <Dropdown
                  id="banner-variant"
                  name="banner-variant"
                  value={bannerColor}
                  onChange={(e) => setBannerColor(e.target.value)}
                >
                  {bannerColorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Dropdown>
              </FormGroup>
            )}

            {type === "banner" && (
              <>
                <FormGroup>
                  <Label htmlFor="state">State</Label>
                  <Dropdown
                    id="state"
                    name="state"
                    value={state}
                    onChange={handleStateChange}
                  >
                    {stateOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Dropdown>
                </FormGroup>
              </>
            )}

            {type === "bottom-sheet" && (
              <>
                <FormGroup>
                  <Label htmlFor="bottom-sheet-variant">Variant</Label>
                  <Dropdown
                    id="bottom-sheet-variant"
                    name="bottom-sheet-variant"
                    value={bottomSheetColor}
                    onChange={(e) => setBottomSheetColor(e.target.value)}
                  >
                    {bottomSheetColorOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Dropdown>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="bottom-sheet-illustration">
                    Illustration
                  </Label>
                  <Dropdown
                    id="bottom-sheet-illustration"
                    name="bottom-sheet-illustration"
                    value={bottomSheetIllustration}
                    onChange={(e) => setBottomSheetIllustration(e.target.value)}
                  >
                    {bottomSheetIllustrationOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Dropdown>
                </FormGroup>
              </>
            )}

            {(type === "banner" || type === "bottom-sheet") && (
              <>
                <FormGroup>
                  <Label htmlFor="header">Header</Label>
                  <Input
                    id="header"
                    name="header"
                    value={header}
                    onChange={handleHeaderChange}
                    placeholder="Enter header"
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="subheader">Subheader</Label>
                  <TextArea
                    id="subheader"
                    name="subheader"
                    value={subheader}
                    onChange={handleSubheaderChange}
                    placeholder="Enter subheader"
                    $size="small"
                  />
                </FormGroup>

                {type === "banner" && (
                  <FormGroup>
                    <Label htmlFor="banner-button-text">Button text</Label>
                    <Input
                      id="banner-button-text"
                      name="banner-button-text"
                      value={bannerButtonText}
                      onChange={handleButtonTextChange}
                      placeholder="Enter button text"
                    />
                  </FormGroup>
                )}
              </>
            )}

            {type === "bottom-sheet" && (
              <>
                <FormGroup>
                  <Label htmlFor="bottom-sheet-primary-button">
                    Button text
                  </Label>
                  <Input
                    id="bottom-sheet-primary-button"
                    name="bottom-sheet-primary-button"
                    value={bottomSheetPrimaryButtonText}
                    onChange={handleBottomSheetPrimaryButtonTextChange}
                    placeholder="Enter button text"
                  />
                </FormGroup>
              </>
            )}

            {type === "notification-center" && (
              <>
                <FormGroup>
                  <Label htmlFor="notification-center-color">Color</Label>
                  <Dropdown
                    id="notification-center-color"
                    name="notification-center-color"
                    value={notificationCenterColor}
                    onChange={(e) => setNotificationCenterColor(e.target.value)}
                  >
                    {notificationCenterColorOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Dropdown>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="notification-center-icon">Icon</Label>
                  <Dropdown
                    id="notification-center-icon"
                    name="notification-center-icon"
                    value={notificationCenterIcon}
                    onChange={(e) => setNotificationCenterIcon(e.target.value)}
                  >
                    {notificationCenterIconOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Dropdown>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="notification-center-unread">Unread</Label>
                  <Dropdown
                    id="notification-center-unread"
                    name="notification-center-unread"
                    value={notificationCenterUnread ? "true" : "false"}
                    onChange={(e) =>
                      setNotificationCenterUnread(e.target.value === "true")
                    }
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Dropdown>
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="notification-center-timestamp">Timestamp</Label>
                  <Dropdown
                    id="notification-center-timestamp"
                    name="notification-center-timestamp"
                    value={notificationCenterTimestamp}
                    onChange={(e) =>
                      setNotificationCenterTimestamp(e.target.value)
                    }
                  >
                    {notificationCenterTimestampOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Dropdown>
                </FormGroup>
              </>
            )}

            {(type === "push-notification" ||
              type === "notification-center") && (
              <>
                <FormGroup>
                  <Label htmlFor="notification-title">Title</Label>
                  <Input
                    id="notification-title"
                    name="notification-title"
                    value={notificationHeader}
                    onChange={handleNotificationHeaderChange}
                    placeholder="Enter notification title"
                  />
                  <CharacterCount
                    $overLimit={
                      notificationHeader.length >
                      (type === "notification-center" ? 35 : 30)
                    }
                  >
                    {notificationHeader.length} of{" "}
                    {type === "notification-center" ? 35 : 30} characters
                  </CharacterCount>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="notification-body">Body</Label>
                  <TextArea
                    id="notification-body"
                    name="notification-body"
                    value={notificationSubheader}
                    onChange={handleNotificationSubheaderChange}
                    placeholder="Enter notification body"
                    $size="small"
                  />
                  <CharacterCount
                    $overLimit={notificationSubheader.length > 120}
                  >
                    {notificationSubheader.length} of 120 characters
                  </CharacterCount>
                </FormGroup>
              </>
            )}
          </Form>
        </Card>
        <Card
          style={{
            flex: "2",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <MobileSimulatorDevice
            bannerVisible={type === "banner"}
            bannerColor={bannerColor}
            bannerHeader={header}
            bannerSubheader={subheader}
            bannerButtonText={bannerButtonText}
            bannerState={state}
            notificationVisible={type === "push-notification"}
            notificationCenterVisible={type === "notification-center"}
            notificationCenterColor={notificationCenterColor}
            notificationCenterIcon={notificationCenterIcon}
            notificationCenterUnread={notificationCenterUnread}
            notificationCenterTimestamp={notificationCenterTimestamp}
            bottomSheetVisible={type === "bottom-sheet"}
            bottomSheetColor={bottomSheetColor}
            bottomSheetHeader={header}
            bottomSheetSubheader={subheader}
            bottomSheetPrimaryButtonText={bottomSheetPrimaryButtonText}
            bottomSheetIllustration={bottomSheetIllustration}
            notificationHeader={notificationHeader}
            notificationSubheader={notificationSubheader}
          />
        </Card>
      </SimulatorContent>
    </>
  );
}

export default MobileSimulator;
